import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ReactCrop from 'react-image-crop';
import equals from 'ramda/src/equals';
import Button from '@components/@core/AnimatedButton';
import CheckBox from '@components/@core/CheckBox';

// TODO; This eventually needs to replace the main modal system
// Simple Bootstrap modal wrapper - NOT the main modal system
const SimpleModal = ({ show, onHide, children, className = '', size = '' }) => {
    if (!show) return null;
    
    return (
        <div className={`modal fade show d-block ${className}`} tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className={`modal-dialog ${size === 'lg' ? 'modal-lg' : ''} modal-dialog-centered`}>
                <div className="modal-content">
                    <div className="modal-header">
                        <button type="button" className="btn-close" onClick={onHide} aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

const DEFAULTCROPS = {
    1: { unit: '%', width: 30, aspect: 1 }, // for avatar image
    2: { unit: '%', width: 30, aspect: 4 / 3 },
    3: { unit: '%', width: 30, aspect: 16 / 9 },
    4: { unit: '%', width: 229, aspect: 229 / 44 }, // for cover image
    5: { unit: '%', width: 43, aspect: 431 / 344 }, // for title image
};

class ImagePicker extends PureComponent {
    state = {
        checked: this.props.type,
        crop: DEFAULTCROPS[this.props.type],
        submitted: false,
        source: this.props.source,
    };

    componentDidUpdate(prevProps, prevState) {
        if (!equals(this.props.source, prevProps.source) && !equals(this.state.source, this.props.source)) {
            this.setState({ submitted: false });
        }
    }

    async makeClientCrop(crop) {
        if (this.imageRef && crop.width && crop.height) {
            const croppedImageUrl = await this.getCroppedImg(this.imageRef, crop, 'newFile.jpeg');
            this.setState({ croppedImageUrl });
        }
    }

    getCroppedImg(image, crop, fileName) {
        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const ctx = canvas.getContext('2d');

        const pixelRatio = window.devicePixelRatio;
        canvas.width = scaleX * crop.width * pixelRatio;
        canvas.height = scaleY * crop.height * pixelRatio;

        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, crop.width * scaleX, crop.height * scaleY);

        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (!blob) {
                    // reject(new Error('Canvas is empty'));
                    console.error('Canvas is empty');
                    return;
                }
                blob.name = fileName;
                URL.revokeObjectURL(this.fileUrl);
                this.fileUrl = URL.createObjectURL(blob);
                resolve(this.fileUrl);
            }, 'image/jpeg');
        });
    }

    getCroppedBlob(image, crop, fileName) {
        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const ctx = canvas.getContext('2d');

        const pixelRatio = window.devicePixelRatio;
        canvas.width = scaleX * crop.width * pixelRatio;
        canvas.height = scaleY * crop.height * pixelRatio;

        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, crop.width * scaleX, crop.height * scaleY);

        return new Promise((resolve, reject) => {
            if (this.props.returnBy === 'Blob') {
                canvas.toBlob((blob) => {
                    if (!blob) {
                        console.error('Canvas is empty');
                        resolve(null);
                        return;
                    }
                    blob.name = fileName;
                    resolve(blob);
                }, 'image/png');
            } else {
                const base64Image = canvas.toDataURL('image/png');
                resolve(base64Image);
            }
        });
    }

    // If you setState the crop in here you should return false.
    handleImageLoaded = (image) => {
        this.imageRef = image;
    };

    handleCropComplete = (crop) => {
        this.makeClientCrop(crop);
    };

    handleCropChange = (crop, percentCrop) => {
        // You could also use percentCrop:
        // this.setState({ crop: percentCrop });
        if (crop.height && crop.width) {
            this.setState({ crop });
        }
    };

    handleApply = async () => {
        this.handleCropComplete(this.state.crop);
        this.handleHide();
        this.setState({ source: this.state.croppedImageUrl });
        if (this.props.onApply) {
            this.props.onApply(this.state.croppedImageUrl);
        }
        if (this.props.onComplete) {
            const blob = await this.getCroppedBlob(this.imageRef, this.state.crop, 'newFile.jpeg');
            this.props.onComplete(blob);
        }
    };

    handleCancel = () => {
        this.handleHide();
    };

    handleHide = () => {
        this.setState({ submitted: true });
    };

    handleCheck = (id) => (e) => {
        this.setState({ checked: id, crop: DEFAULTCROPS[id] });
    };

    render() {
        const { source, disabled } = this.props;
        const { checked, crop, submitted } = this.state;
        const show = !!source && !submitted && source !== '/images/default.png';

        return (
            <SimpleModal className="ds-imagepicker" show={show} size="lg" onHide={this.handleHide}>
                <div className="crop-content">
                    <ReactCrop src={source} crop={crop} ruleOfThirds onImageLoaded={this.handleImageLoaded} onComplete={this.handleCropComplete} onChange={this.handleCropChange} />
                    <div className="d-h-between pb-2 pt-2 flex-column flex-sm-row w-100">
                        <div className="d-flex ms-3">
                            {!disabled && (
                                <>
                                    <CheckBox id="crop1" className="mr-3" checked={checked === '1'} size="regular" title="1 : 1" onChange={this.handleCheck('1')} />
                                    <CheckBox id="crop2" className="mr-3" checked={checked === '2'} size="regular" title="4 : 3" onChange={this.handleCheck('2')} />
                                    <CheckBox id="crop3" className="mr-3" checked={checked === '3'} size="regular" title="16 : 9" onChange={this.handleCheck('3')} />
                                    <CheckBox id="crop4" className="mr-3" checked={checked === '4'} size="regular" title="1145 : 220" onChange={this.handleCheck('4')} />
                                    <CheckBox id="crop5" className="mr-3" checked={checked === '5'} size="regular" title="431 : 344" onChange={this.handleCheck('5')} />
                                </>
                            )}
                        </div>
                        <div className="d-flex ms-3 ms-sm-0 mt-2 mt-sm-0">
                            <Button className="mr-2" type="low" name="Discard" onClick={this.handleCancel} />
                            <Button className="ml-2 me-3" name="Apply" onClick={this.handleApply} />
                        </div>
                    </div>
                </div>
            </SimpleModal>
        );
    }
}

ImagePicker.propTypes = {
    source: PropTypes.any,
    type: PropTypes.oneOf(['1', '2', '3', '4', '5']),
    disabled: PropTypes.bool,
    returnBy: PropTypes.oneOf(['Blob', 'DataURL']),
};

ImagePicker.defaultProps = {
    source: null,
    type: '1',
    disabled: false,
    returnBy: 'Blob',
};

export default ImagePicker;
